import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, LockOpen, Loader2, Edit, Save, X, Archive, ArchiveRestore, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MarkdownView } from './MarkdownView';
import { JournalAttachments } from './JournalAttachments';
import {
  useUpdateJournalEntry,
  useAcquireJournalLock,
  useReleaseJournalLock,
  useJournalHeartbeat,
  type JournalEntryWithAuthor,
  type JournalLockRow,
} from '@/hooks/useJournal';
import { cn } from '@/utils/cn';

function wasUpdated(entry: JournalEntryWithAuthor): boolean {
  return new Date(entry.updated_at).getTime() > new Date(entry.created_at).getTime() + 1000;
}

interface JournalEntryInlineFeedProps {
  entry: JournalEntryWithAuthor;
  locks: JournalLockRow[];
  clinicId: string;
  patientId: string;
  currentUserId: string | null | undefined;
  isAdmin: boolean;
  onArchive: () => void;
  onRestore: () => void;
  scrollToId: string | null;
  onScrollComplete: () => void;
}

export function JournalEntryInlineFeed({
  entry,
  locks,
  clinicId,
  patientId,
  currentUserId,
  isAdmin,
  onArchive,
  onRestore,
  scrollToId,
  onScrollComplete,
}: JournalEntryInlineFeedProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(entry.title ?? '');
  const [content, setContent] = useState(entry.content_md);
  const [hasLock, setHasLock] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const entryRef = useRef<HTMLDivElement>(null);

  const lock = locks.find((l) => l.entry_id === entry.id);
  const isLockedByOther = lock && lock.user_id !== currentUserId;

  const updateEntry = useUpdateJournalEntry(clinicId);
  const acquireLock = useAcquireJournalLock(clinicId);
  const releaseLock = useReleaseJournalLock(clinicId);

  const handleLockLost = () => {
    setHasLock(false);
    toast.error(t('journal.lock_lost'));
  };

  useJournalHeartbeat(entry.id, hasLock, handleLockLost);

  useEffect(() => {
    return () => {
      if (hasLock) {
        void releaseLock.mutateAsync(entry.id).catch(() => undefined);
      }
    };
  }, [entry.id, hasLock]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollToId === entry.id && entryRef.current) {
      entryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onScrollComplete();
    }
  }, [scrollToId, entry.id, onScrollComplete]);

  const handleAcquireLock = async () => {
    const result = await acquireLock.mutateAsync(entry.id);
    if (result.acquired) {
      setHasLock(true);
      setTitle(entry.title ?? '');
      setContent(entry.content_md);
    } else {
      toast.error(t('journal.lock_held_by', { name: result.holder_user_name }));
    }
  };

  const handleSave = async () => {
    await updateEntry.mutateAsync({ id: entry.id, title, content_md: content });
    toast.success(t('journal.saved'));
    await releaseLock.mutateAsync(entry.id);
    setHasLock(false);
  };

  const handleCancel = async () => {
    await releaseLock.mutateAsync(entry.id);
    setHasLock(false);
    setTitle(entry.title ?? '');
    setContent(entry.content_md);
  };

  const contentPreview = entry.content_md.length > 500 && !isExpanded
    ? entry.content_md.slice(0, 500) + '...'
    : entry.content_md;

  const showExpandButton = entry.content_md.length > 500;

  return (
    <div
      ref={entryRef}
      className={cn(
        'rounded-lg border bg-white p-6 shadow-sm transition-all dark:border-gray-700 dark:bg-card-dark',
        entry.archived_at && 'opacity-60',
        scrollToId === entry.id && 'ring-2 ring-indigo-500',
      )}
    >
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex-1">
          {hasLock ? (
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('journal.title_placeholder')}
              className="text-lg font-semibold"
            />
          ) : (
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {entry.title || t('journal.untitled')}
            </h3>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{format(new Date(entry.entry_date), 'PPP')}</span>
            <span>•</span>
            <span>{t(`journal.type_${entry.entry_type}`)}</span>
            {entry.author_name && (
              <>
                <span>•</span>
                <span>{entry.author_name}</span>
              </>
            )}
            {wasUpdated(entry) && (
              <>
                <span>•</span>
                <span>{t('journal.updated_at', { date: format(new Date(entry.updated_at), 'PPp') })}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {hasLock ? (
            <span className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400">
              <Lock className="h-3.5 w-3.5" />
              {t('journal.you_have_lock')}
            </span>
          ) : isLockedByOther ? (
            <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <Lock className="h-3.5 w-3.5" />
              {t('journal.lock_held_by', { name: lock!.user_name_snapshot })}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <LockOpen className="h-3.5 w-3.5" />
            </span>
          )}

          {entry.archived_at && (
            <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              <Archive className="h-3 w-3" />
              {t('journal.archived')}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        {hasLock ? (
          <textarea
            className="block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={12}
            placeholder={t('journal.content_placeholder')}
          />
        ) : (
          <div>
            <MarkdownView content={contentPreview} />
            {showExpandButton && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" />
                    {t('journal.show_less', 'Show less')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" />
                    {t('journal.show_more', 'Show more')}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Attachments */}
      <JournalAttachments
        clinicId={clinicId}
        patientId={patientId}
        entryId={entry.id}
        canEdit={hasLock && !entry.archived_at}
      />

      {/* Actions */}
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
        <div>
          {isAdmin && !hasLock && (
            <>
              {entry.archived_at ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRestore}
                >
                  <ArchiveRestore className="mr-1.5 h-4 w-4" />
                  {t('journal.restore')}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onArchive}
                >
                  <Archive className="mr-1.5 h-4 w-4" />
                  {t('journal.archive')}
                </Button>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasLock ? (
            <>
              <Button variant="secondary" onClick={handleCancel} size="sm" disabled={updateEntry.isPending}>
                <X className="mr-1.5 h-4 w-4" />
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} size="sm" disabled={updateEntry.isPending}>
                {updateEntry.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Save className="mr-1.5 h-4 w-4" />
                    {t('journal.save')}
                  </>
                )}
              </Button>
            </>
          ) : (
            !isLockedByOther && !entry.archived_at && (
              <Button onClick={handleAcquireLock} size="sm" disabled={acquireLock.isPending}>
                {acquireLock.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Edit className="mr-1.5 h-4 w-4" />
                    {t('journal.edit')}
                  </>
                )}
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
